import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import {
  BUSINESS_NAME_LINE_1,
  BUSINESS_NAME_LINE_2,
  NAV_ITEMS,
  TOPBAR_MESSAGES,
} from "./homepage-content";
import {
  buildBusinessContactLinks,
  formatPhoneForDisplay,
  type BusinessContactSettings,
} from "./business-contact";
import { formatHebrewItemCount } from "../hebrew-copy";
import { Icon } from "./homepage-icons";
import { BrandMark } from "./BrandMark";
import {
  clampActiveResultIndex,
  searchHomepageIndex,
  type SearchResult,
} from "./homepage-search";

export function SiteHeader(props: {
  totalQuantity: number;
  businessSettings: BusinessContactSettings;
  publicProductSlugs?: readonly string[] | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const drawerRef = useRef<HTMLDialogElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  const publicProductSlugs =
    props.publicProductSlugs == null
      ? null
      : new Set(props.publicProductSlugs);
  const results: SearchResult[] = searchHomepageIndex(
    query,
    8,
    publicProductSlugs,
  );
  const contactLinks = buildBusinessContactLinks(props.businessSettings);
  const displayPhone = formatPhoneForDisplay(props.businessSettings.phone);

  useEffect(() => {
    if (!menuOpen) return;
    const drawer = drawerRef.current;
    if (drawer?.showModal) drawer.showModal();
    else drawer?.setAttribute("open", "");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const desktop = window.matchMedia?.("(min-width: 1024px)");
    const onDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };
    desktop?.addEventListener("change", onDesktop);
    return () => {
      if (drawer?.close) drawer.close();
      else drawer?.removeAttribute("open");
      desktop?.removeEventListener("change", onDesktop);
      document.body.style.overflow = previousOverflow;
      menuButtonRef.current?.focus();
    };
  }, [menuOpen]);

  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (menuOpen) setMenuOpen(false);
      if (searchOpen) {
        setSearchOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setSearchOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const goToResult = (result: SearchResult) => {
    window.location.href = result.href;
    setSearchOpen(false);
    setQuery("");
    setActiveIndex(-1);
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSearchOpen(true);
      setActiveIndex((previous) =>
        clampActiveResultIndex(previous + 1, results.length),
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSearchOpen(true);
      setActiveIndex((previous) =>
        clampActiveResultIndex(previous - 1, results.length),
      );
    } else if (event.key === "Enter") {
      if (activeIndex >= 0 && results[activeIndex]) {
        event.preventDefault();
        goToResult(results[activeIndex]);
      }
    } else if (event.key === "Escape") {
      setSearchOpen(false);
      setActiveIndex(-1);
    }
  };

  const listboxId = "homepage-search-listbox";

  return (
    <header className="hp-header">
      <div className="hp-topbar" aria-hidden="true">
        <div className="hp-topbar-inner">
          {TOPBAR_MESSAGES.map((message) => (
            <span key={message} className="hp-topbar-item">
              {message}
            </span>
          ))}
        </div>
      </div>
      <div className="hp-header-row">
        <button
          type="button"
          ref={menuButtonRef}
          className="hp-menu-button"
          aria-label={menuOpen ? "סגירת תפריט" : "פתיחת תפריט"}
          aria-expanded={menuOpen}
          aria-controls="hp-mobile-drawer"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Icon name={menuOpen ? "close" : "menu"} />
        </button>

        <a
          className="hp-brand"
          href="/"
          aria-label={`${BUSINESS_NAME_LINE_1} ${BUSINESS_NAME_LINE_2} — דף הבית`}
        >
          <BrandMark />
          <span className="hp-brand-name">
            <span>{BUSINESS_NAME_LINE_1}</span>
            <span>{BUSINESS_NAME_LINE_2}</span>
          </span>
        </a>

        <div className="hp-search" ref={searchRef}>
          <Icon name="search" size={20} className="hp-search-icon" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={searchOpen && results.length > 0}
            aria-controls={listboxId}
            aria-activedescendant={
              activeIndex >= 0 ? `hp-search-option-${activeIndex}` : undefined
            }
            aria-autocomplete="list"
            aria-label="חיפוש מוצרים"
            className="hp-search-input"
            placeholder="מה תרצו להדפיס?"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSearchOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={onSearchKeyDown}
          />
          {query ? (
            <button
              type="button"
              className="hp-search-clear"
              aria-label="ניקוי חיפוש"
              onClick={() => {
                setQuery("");
                setActiveIndex(-1);
                inputRef.current?.focus();
              }}
            >
              <Icon name="close" size={16} />
            </button>
          ) : null}

          {searchOpen && query ? (
            <div
              className="hp-search-panel"
              role="listbox"
              id={listboxId}
              aria-label="תוצאות חיפוש"
            >
              {results.length === 0 ? (
                <p className="hp-search-empty">לא נמצאו מוצרים מתאימים</p>
              ) : (
                results.map((result, index) => (
                  <a
                    key={result.id}
                    id={`hp-search-option-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`hp-search-option${index === activeIndex ? " is-active" : ""}`}
                    href={result.href}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={(event) => {
                      event.preventDefault();
                      goToResult(result);
                    }}
                  >
                    <span className="hp-search-option-icon" aria-hidden="true">
                      <Icon
                        name={
                          result.id === "search-pvc-sign" ? "signs" : "search"
                        }
                        size={16}
                      />
                    </span>
                    <span className="hp-search-option-text">
                      <span>{result.label}</span>
                      <small>{result.category}</small>
                    </span>
                  </a>
                ))
              )}
            </div>
          ) : null}
        </div>

        <div className="hp-header-actions">
          <div className="hp-header-links">
            <a
              className="hp-header-action hp-header-action--desktop-only"
              href="/account#orders"
            >
              <Icon name="tracking" size={18} />
              <span>מעקב הזמנה</span>
            </a>
            <a
              className="hp-header-action hp-header-action--desktop-only"
              href="/account"
            >
              <Icon name="account" size={18} />
              <span>אזור אישי</span>
            </a>
          </div>
          <div className="hp-header-cart-group">
            {contactLinks.whatsappHref ? (
              <a
                className="hp-contact-whatsapp-button"
                href={contactLinks.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="פנייה בוואטסאפ"
                title="פנייה בוואטסאפ"
              >
                <Icon name="whatsapp" size={22} />
              </a>
            ) : null}
            <a
              className="hp-cart-button"
              href="/cart"
              aria-label={`סל הקניות, ${formatHebrewItemCount(props.totalQuantity)}`}
            >
              <Icon name="cart" size={22} />
              <strong>{props.totalQuantity}</strong>
            </a>
          </div>
        </div>
      </div>

      <nav className="hp-nav" aria-label="ניווט ראשי">
        {NAV_ITEMS.map((item) => (
          <a key={item.id} href={item.href}>
            {item.label}
          </a>
        ))}
      </nav>

      <dialog
        ref={drawerRef}
        id="hp-mobile-drawer"
        className={`hp-drawer${menuOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="תפריט ניווט"
        hidden={!menuOpen}
        onCancel={(event) => {
          event.preventDefault();
          setMenuOpen(false);
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) setMenuOpen(false);
        }}
      >
        <div className="hp-drawer-header">
          <span className="hp-drawer-title">תפריט</span>
          <button
            type="button"
            className="hp-drawer-close"
            aria-label="סגירת תפריט"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <nav aria-label="ניווט נייד">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={item.href}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="hp-drawer-actions">
          <a
            className="hp-drawer-cart"
            href="/cart"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="cart" size={20} />
            <span>סל הקניות · {formatHebrewItemCount(props.totalQuantity)}</span>
          </a>
          <a
            className="hp-drawer-account-button"
            href="/account"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="account" size={18} />
            <span>אזור אישי</span>
          </a>
          <a
            className="hp-drawer-account-button"
            href="/account#orders"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="tracking" size={18} />
            <span>מעקב הזמנה</span>
          </a>
        </div>
        {contactLinks.phoneHref ||
        contactLinks.whatsappHref ||
        contactLinks.emailHref ? (
          <div className="hp-drawer-contact" aria-label="יצירת קשר">
            {contactLinks.phoneHref && displayPhone ? (
              <a
                className="hp-drawer-contact-link"
                href={contactLinks.phoneHref}
              >
                טלפון: {displayPhone}
              </a>
            ) : null}
            {contactLinks.whatsappHref ? (
              <a
                className="hp-drawer-contact-link"
                href={contactLinks.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="whatsapp" size={19} />
                <span>WhatsApp</span>
              </a>
            ) : null}
            {contactLinks.emailHref && props.businessSettings.email ? (
              <a
                className="hp-drawer-contact-link"
                href={contactLinks.emailHref}
              >
                {props.businessSettings.email}
              </a>
            ) : null}
          </div>
        ) : null}
      </dialog>
    </header>
  );
}
